import { FC, useEffect, useMemo } from "react";
import { getScreenSize } from "@utils/getScreenSize";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { useWallet } from "@stores/wallet.store";
import { usePools } from "@stores/pools.store";
import { useSwap } from "@stores/swap.store";
import BigButton from "@components/shared/card/BigButton";

interface Props {
    handleSwap: () => void;
    isFastExchange: boolean;
    isPending?: boolean;
    disabled?: boolean;
    isCalculatingPrice?: boolean;
    isMnemonic?: boolean;
}

const SwapButton: FC<Props> = ({
    handleSwap,
    isFastExchange,
    isPending,
    disabled = false,
    isCalculatingPrice = false,
    isMnemonic = false,
}) => {
    const { isMobile } = getScreenSize();
    const [tonConnectUI] = useTonConnectUI();
    const isConnected = useWallet((state) => state.isConnected);
    const canSwapInternal = useSwap((state) => state.canSwap()); // Renamed for clarity
    const isBalanceSufficient = useSwap((state) => state.isBalanceSufficient());
    const sellAmount = useSwap((state) => state.sellAssetInfo.amount);
    const poolsLoading = usePools((state) => state.isLoading);
    const emulationResults = usePools((state) => state.emulationResults);

    // Effect for spacebar swap trigger (logic remains largely the same)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === " ") {
                event.preventDefault();
                // Determine internal ability to swap based on state
                const shouldTriggerSwap =
                    isConnected &&
                    canSwapInternal &&
                    !isPending &&
                    !isCalculatingPrice &&
                    !poolsLoading &&
                    isBalanceSufficient &&
                    !!emulationResults &&
                    emulationResults.length > 0;
                if (shouldTriggerSwap) {
                    handleSwap();
                } else if (!isConnected) {
                    tonConnectUI?.openModal();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        isConnected,
        canSwapInternal,
        isPending,
        isCalculatingPrice,
        poolsLoading,
        isBalanceSufficient,
        emulationResults,
        handleSwap,
        tonConnectUI,
    ]);

    // --- Determine button state --- (Text remains similar)
    const buttonText = useMemo(() => {
        if (!isConnected) return "Connect Wallet";
        if (isPending)
            return (
                <span className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></span>
            );
        if (isCalculatingPrice || poolsLoading) return "Calculating price...";
        if (!sellAmount || parseFloat(sellAmount) <= 0) return "Enter an amount";
        if (!isBalanceSufficient) return "Insufficient balance";
        if (!emulationResults || emulationResults.length === 0) return "No liquidity available";
        if (!canSwapInternal) return "Select assets"; // Added state for when assets aren't selected
        return "Swap";
    }, [
        isConnected,
        isPending,
        isCalculatingPrice,
        poolsLoading,
        sellAmount,
        isBalanceSufficient,
        emulationResults,
        canSwapInternal,
    ]);

    // Combined internal and external disabled state
    const isButtonDisabled = useMemo(() => {
        if (!isConnected) return false; // Connect button is never disabled by logic here
        // Disable if externally requested OR if any swap-blocking condition is met
        return (
            disabled ||
            isPending ||
            isCalculatingPrice ||
            poolsLoading ||
            !canSwapInternal ||
            !isBalanceSufficient ||
            !sellAmount ||
            parseFloat(sellAmount) <= 0 ||
            !emulationResults ||
            emulationResults.length === 0
        );
    }, [
        isConnected,
        disabled,
        isPending,
        isCalculatingPrice,
        poolsLoading,
        canSwapInternal,
        isBalanceSufficient,
        sellAmount,
        emulationResults,
    ]);

    // --- Determine button appearance variant --- << REFACTORED
    const buttonVariant = useMemo(() => {
        if (!isConnected) return "connect";
        if (isPending || isCalculatingPrice || poolsLoading) return "loading";
        // Error states
        if (!sellAmount || parseFloat(sellAmount) <= 0) return "error";
        if (!isBalanceSufficient) return "error";
        if (!emulationResults || emulationResults.length === 0) return "error";
        if (!canSwapInternal) return "error"; // Assets not selected is an error state for swapping
        if (!isMnemonic) return "ton";
        if (isFastExchange) return "fast";
        return "default";
    }, [
        isConnected,
        isPending,
        isCalculatingPrice,
        poolsLoading,
        sellAmount,
        isBalanceSufficient,
        emulationResults,
        canSwapInternal,
        isFastExchange,
    ]);

    // --- Determine onClick behavior --- (remains similar)
    const handleClick = () => {
        if (!isConnected) {
            tonConnectUI?.openModal();
        } else if (!isButtonDisabled) {
            // Only allow swap if not disabled
            handleSwap();
        }
        // Do nothing if connected but button is disabled
    };

    // Determine if space hint should be shown
    const showSpaceHint = useMemo(() => {
        // Show hint only when swap is possible and not in error/loading states
        return (
            isFastExchange &&
            !isMobile &&
            isConnected &&
            !isButtonDisabled &&
            buttonVariant !== "loading" &&
            buttonVariant !== "error"
        );
    }, [isFastExchange, isMobile, isConnected, isButtonDisabled, buttonVariant]);

    return (
        <BigButton
            text={buttonText}
            onClick={handleClick}
            disabled={isButtonDisabled}
            variant={buttonVariant}
            size="default"
            showSpaceHint={showSpaceHint}
        />
    );
};

export default SwapButton;
